// Imports
// --------------------------------------------------------------------------------
import type React from 'react';
import { createRoot, type Root } from 'react-dom/client';

// Class Definition
// --------------------------------------------------------------------------------
export type CacheContents = {
  el: HTMLElement;
  root: Root;
};
export type ServiceCache = Map<string, CacheContents>;

/**
 * Enables functionality around wrapping JSX into HTML and managing the entries.
 * Primarily used with forge constructors/templates that require HTML
 *
 * **IMPORTANT**
 *
 * If you are writing tests for a component which uses this service and are getting console errors about
 *
 *  **"Warning: An update to Root inside a test was not wrapped in act(...)"**
 *
 * relating to the `_unmountRoot()` method of the service, adding the following import to your test file
 *  BEFORE importing the component which uses the service should resolve the errors:
 *
 * `import 'common/tests/helpers/makeJsxToHtmlElementServiceTestSafe';`
 *
 */
export class JsxToHtmlElementService {
  // Setup
  // --------------------------------------------------------------------
  cache: ServiceCache;

  constructor(cache?: ServiceCache) {
    // This has to be set explicitly here. Anywhere else should use the `setCache()` method
    this.cache = cache || this.createCache();
  }

  // Instance Methods
  // --------------------------------------------------------------------

  /**
   * Wrap JSX into an HTML Element and add it to the cache
   * @param jsx React.ReactElement
   * @param key string
   * @param elementType string [optional: Default is 'div']
   * @returns HTMLElement
   */
  wrapJsx = (jsx: React.ReactElement, key: string, elementType?: string) => {
    const el = this.getEl(key) || document.createElement(elementType || 'div');
    const root = this.getRoot(key) || createRoot(el);

    root.render(jsx);

    this.set(key, el, root);

    return el as HTMLElement;
  };

  // Cache Specific
  // ----------------------------------------------------------

  /**
   * Create and return a new cache Map.
   * @returns ServiceCache
   */
  createCache = () => {
    return new Map<string, CacheContents>();
  };

  /**
   * Return the existing cache.
   * @returns ServiceCache
   */
  getCache = () => {
    return this.cache as ServiceCache;
  };

  /**
   * Set the instance's cache to the one provided only if there is no current cache
   *  or the current cache is empty and can safely be replaced.
   *
   * NOTE: There should only ever be one scenario where there is no instance cache:
   *   1) As part of the overwriteCache method  (a new cache is immediately set after the current is removed)
   * @param cache ServiceCache
   * @returns JsxToHtmlElementService
   */
  setCache = (cache: ServiceCache) => {
    if (!this.cache || !this.cache.size) {
      this.cache = cache;
    }

    return this;
  };

  /**
   * Set the instance's cache to the one provided or create a new one.
   * Unmounts and deletes entries in the current cache beforehand unless the override is truthy.
   * @param cache ServiceCache [optional]
   * @param overrideDelete boolean [optional] Default: false
   * @returns JsxToHtmlElementService
   */
  overwriteCache = (cache?: ServiceCache, overrideDelete = false) => {
    // Unmount and delete any elements in the current cache unless overridden.
    if (!overrideDelete) {
      this.deleteAll();
    }

    // Remove the existing cache
    Reflect.deleteProperty(this, 'cache');

    // Set the provided cache or create a new one.
    return this.setCache(cache || this.createCache());
  };

  // Get/Set
  // ----------------------------------------------------------
  /**
   * Get a specific contents object within the instance cache by its key
   * @param key string
   * @returns CacheContents | undefined
   */
  get = (key: string) => {
    return this.cache.get(key);
  };

  /**
   * Get a specific element within the instance cache by its key
   * @param key string
   * @returns HTMLElement | undefined
   */
  getEl = (key: string) => {
    return this.cache.get(key)?.el;
  };

  /**
   * Get a specific root within the instance cache by its key
   * @param key string
   * @returns Root | undefined
   */
  getRoot = (key: string) => {
    return this.cache.get(key)?.root;
  };

  /**
   * Set a specific key/element pair within the instance cache
   * @param key string
   * @param el HTMLElement
   * @returns JsxToHtmlElementService
   */
  set = (key: string, el: HTMLElement, root: Root) => {
    // Set the new element
    this.cache.set(key, { el, root });

    return this;
  };

  // Unmount - Remove Element from DOM but not the cache
  // ----------------------------------------------------------
  /**
   * Unmounts the provided react root node.
   *
   * This was broken out to enable a test safe version that wraps the unmount in RTL's `act(...)`.
   *  A test safe version of the full `unmount` method could have been implemented instead,
   *  but by breaking this out, we only have to replace the bare minimum to avoid test issues
   *  and can avoid duplicating any actual service logic.
   * @param root A react root node to unmount
   */
  private _unmountRoot = (root: Root) => {
    root.unmount();
  };

  /**
   * Unmount a specific key within the instance cache
   * @param key string
   * @returns JsxToHtmlElementService
   */
  unmount = (key: string) => {
    const contents = this.cache.get(key);

    contents?.el.remove();

    if (contents?.root) {
      // This gets us out of the current rerender cycle to avoid React complaining.
      //  The complaint isn't breaking, but don't know if that will stay true in
      //  newer React versions.
      queueMicrotask(() => this._unmountRoot(contents.root));
    }

    return this;
  };

  /**
   * Unmount a selection of keys within the instance cache
   * @param keys Array<string>
   * @returns JsxToHtmlElementService
   */
  unmountSelection = (keys: Array<string>) => {
    if (keys.length) {
      keys.forEach((key: string) => {
        this.unmount(key);
      }, this);
    }

    return this;
  };

  /**
   * Unmount all keys within the instance cache
   * @returns JsxToHtmlElementService
   */
  unmountAll = () => {
    if (this.cache.size) {
      this.unmountSelection(Array.from(this.cache.keys()));
    }

    return this;
  };

  // Delete - Remove Element from DOM and the cache
  // ----------------------------------------------------------

  /**
   * Delete a specific key within the instance cache, making sure it is unmounted first
   * @param key string
   * @returns JsxToHtmlElementService
   */
  delete = (key: string) => {
    if (this.cache.has(key)) {
      // Be sure to unmount an existing element for the key
      this.unmount(key);

      // Delete the element for the key
      this.cache.delete(key);
    }

    return this;
  };

  /**
   * Delete a selection of keys within the instance cache, making sure they are unmounted first
   * @param keys Array<string>
   * @returns JsxToHtmlElementService
   */
  deleteSelection = (keys: Array<string>) => {
    if (keys.length) {
      keys.forEach((key: string) => {
        this.delete(key);
      }, this);
    }

    return this;
  };

  /**
   * Delete all keys within the instance cache, making sure they are unmounted first
   * @returns JsxToHtmlElementService
   */
  deleteAll = () => {
    // Make sure all elements are unmounted first
    this.unmountAll();

    // Clear the cache
    this.cache.clear();

    return this;
  };
}
